// src/components/settings/shared/dialogs/VetDialog.tsx

import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  DailogLabel,
  DialogLabelText,
  StyledTextField,
} from '../../../forms/shared/FormElements';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import { Vet } from '../../../../services/api';

interface VetFormData {
  name: string;
  phone: string;
  email: string;
}

interface VetDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: VetFormData) => void;
  editing: Vet | null;
}

const VetDialog: React.FC<VetDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
}) => {
  const [formData, setFormData] = useState<VetFormData>({
    name: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    if (open) {
      setFormData(
        editing
          ? {
              name: editing.name,
              phone: editing.phone || '',
              email: editing.email || '',
            }
          : { name: '', phone: '', email: '' }
      );
    }
  }, [open, editing]);

  const canSave = formData.name.trim().length > 0;

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Vet' : 'Add Vet'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      maxWidth="sm"
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onSave(formData)}
            disabled={!canSave}
            text={editing ? 'Save' : 'Save'}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={0.5} mt={0.2}>
        <DailogLabel>
          <DialogLabelText id="vet-name-label">Vet Name:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="vet-name-input"
          inputProps={{ 'aria-labelledby': 'vet-name-label' }}
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, name: e.target.value })
          }
          required
          fullWidth
        />
        <DailogLabel>
          <DialogLabelText id="vet-phone-label">Phone:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="vet-phone-input"
          inputProps={{ 'aria-labelledby': 'vet-phone-label' }}
          value={formData.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, phone: e.target.value })
          }
          fullWidth
        />
        <DailogLabel>
          <DialogLabelText id="vet-email-label">Email:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="vet-email-input"
          inputProps={{ 'aria-labelledby': 'vet-email-label' }}
          type="email"
          value={formData.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, email: e.target.value })
          }
          fullWidth
        />
      </Box>
    </DialogBase>
  );
};

export default VetDialog;
